import clsx from 'clsx';
import * as styles from './index.css';
import { Box, Icon, ListItem, PlainLink } from '@customerio/pluma-components/react';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { useTrackEvent } from '@/services/analytics';

interface Args {
  completion: number;
  highlight?: boolean;
  children: React.ReactNode;
  href?: string | null;
  ariaLabel: string;
  onClick?: (event: React.MouseEvent) => void;
}

export function RocketItem({ completion, highlight, children, href, ariaLabel, onClick }: Args) {
  const workspaceId = getWorkspaceIdFromCurrentUrl();
  const isComplete = completion >= 1;
  const safeNavigate = useSafeNavigate();
  const track = useTrackEvent();

  const handleLinkClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    safeNavigate(event.currentTarget.getAttribute('href') ?? '');

    track('Rocket: Clicked on item', {
      'Step Label': ariaLabel,
    });

    onClick?.(event);
  };

  return (
    <ListItem
      py="250"
      px="250"
      // Using a class name to be able to select this element through a query selector
      // to fire confetti when the step is completed.
      // TODO: Replace with a ref when we have a better way to handle this.
      className={clsx('rocket-list-item', styles.rocketListItem, {
        [styles.rocketListItemHighlight]: highlight,
        [styles.rocketListItemComplete]: isComplete,
      })}
    >
      <div className={styles.rocketListItemProgress} style={{ width: `${Math.min(1, completion) * 100}%` }} />
      {href != null ? (
        <PlainLink
          className={styles.rocketListItemLink}
          href={`/workspaces/${workspaceId}/journeys/${href}`}
          onClick={handleLinkClick}
          aria-label={ariaLabel}
        />
      ) : null}
      <div className={styles.rocketListItemContent}>
        <Box display="flex" alignItems="center">
          <Box
            mr="100"
            display="flex"
            alignItems="center"
            justifyContent="center"
            className={clsx(styles.rocketListItemIcon, { [styles.rocketListItemIconComplete]: isComplete })}
          >
            {isComplete ? <Icon name="check" size="sm" /> : null}
          </Box>
          <Box className={styles.rocketListItemText} display="flex" alignItems="center">
            {children}
          </Box>
        </Box>
      </div>
    </ListItem>
  );
}
